import { useCallback, useMemo, useState } from 'react';
import { toast as message } from 'sonner';
import { handleOperationError, updateRecord } from '../../utils/supabase-operations';

/**
 * REG-739 Undo for a grid's edits: a stack, newest first, for the page visit
 * only. Each entry is one write the user made on the grid: an inline cell edit
 * (one record) or a bulk edit (one field on several records). Undo writes each
 * record's previous value back through updateRecord with a compare-and-set on
 * the value the edit wrote, so RLS, column guards and triggers decide exactly
 * as for the edit, and a record someone changed since is left alone.
 *
 * Shared by useTableInlineEdit (WorkspaceGrid pages) and the report-grid
 * Submissions lists (REG-828), so both undo the same way.
 */
export interface EditUndoChange {
  recordId: number | string;
  /** The value before the edit, written back on undo. */
  oldValue: unknown;
  /** The grid row showing the record, when the caller patches rows in place. */
  rowNodeId?: string;
}

export interface EditUndoEntry {
  tableName: string;
  schema: string;
  /** The base-table column that was written. */
  field: string;
  /** Where the grid row keeps the value, when it is not `field`. */
  rowField?: string;
  /** The value the edit wrote (the same on every record). */
  newValue: unknown;
  /** The column's label, for messages. */
  label: string;
  /** PostgreSQL type of `field`, for a reliable compare-and-set. */
  dataType?: string | null;
  changes: EditUndoChange[];
}

export interface EditUndoStack {
  push: (entry: EditUndoEntry) => void;
  canUndo: boolean;
  nextUndoLabel: string | null;
  undoLast: () => Promise<void>;
}

export function displayEditValue(value: unknown): string {
  return value == null || value === '' ? 'blank' : String(value);
}

function describe(entry: EditUndoEntry): string {
  if (entry.changes.length === 1) return `${entry.label} back to ${displayEditValue(entry.changes[0].oldValue)}`;
  return `${entry.label} on ${entry.changes.length} records back to their previous values`;
}

export function useEditUndoStack({
  onUndone,
}: {
  /** After an undo wrote at least one record: patch rows, refetch. */
  onUndone?: (entry: EditUndoEntry, reverted: EditUndoChange[]) => void;
} = {}): EditUndoStack {
  const [history, setHistory] = useState<EditUndoEntry[]>([]);

  const push = useCallback((entry: EditUndoEntry) => {
    if (entry.changes.length === 0) return;
    setHistory((current) => [...current, entry]);
  }, []);

  const undoLast = useCallback(async () => {
    const entry = history[history.length - 1];
    if (!entry) return;
    const reverted: EditUndoChange[] = [];
    let lastError: unknown = null;
    // One record at a time: a bulk edit is a handful of rows, and each write
    // keeps its own compare-and-set and error.
    for (const change of entry.changes) {
      try {
        const { error } = await updateRecord({
          tableName: entry.tableName,
          schema: entry.schema,
          data: { [entry.field]: change.oldValue },
          whereClause: { id: change.recordId },
          options: {
            fieldName: entry.field,
            expectedValues: { [entry.field]: entry.newValue },
            ...(entry.dataType ? { expectedValueTypes: { [entry.field]: entry.dataType } } : {}),
          },
        });
        if (error) throw error;
        reverted.push(change);
      } catch (error) {
        lastError = error;
      }
    }
    if (reverted.length === 0) {
      // Nothing reversed: keep the entry so the user can see what failed and retry.
      message.error(handleOperationError(lastError, 'Could not undo this change').userMessage);
      return;
    }
    setHistory((current) => current.slice(0, -1));
    onUndone?.(entry, reverted);
    if (reverted.length === entry.changes.length) {
      message.success(`Undone: ${describe(entry)}`);
    } else {
      message.warning(`Undone on ${reverted.length} of ${entry.changes.length} records. The others changed since, or could not be written, and were left as they are.`);
    }
  }, [history, onUndone]);

  const last = history[history.length - 1];
  return useMemo(() => ({
    push,
    canUndo: Boolean(last),
    nextUndoLabel: last ? describe(last) : null,
    undoLast,
  }), [last, push, undoLast]);
}
